import { apiErrorSchema } from '@proctoring/shared';
/**
 * A failure carrying the API's `{ error: { code, message } }` envelope.
 *
 * The code is preserved rather than flattened to a status because the UI branches on it:
 * `join_window_closed` is a countdown, `invalid_invitation` is a "request a new link"
 * screen, and `unauthorized` triggers a session teardown — all of which map to 400/401/403
 * and would be indistinguishable from the status alone.
 */
export class ApiRequestError extends Error {
    code;
    status;
    constructor(code, message, status) {
        super(message);
        this.name = 'ApiRequestError';
        this.code = code;
        this.status = status;
    }
    /** True when the token was rejected — the caller should drop the session and re-login. */
    get isAuthFailure() {
        return this.code === 'unauthorized';
    }
}
/** A response whose body did not match the schema the caller expected. */
export class ApiParseError extends Error {
    issues;
    constructor(path, issues) {
        super(`Unexpected response shape from ${path}: ${issues.join('; ')}`);
        this.name = 'ApiParseError';
        this.issues = issues;
    }
}
/** The request never produced a response (offline, DNS failure, CORS rejection). */
export class ApiNetworkError extends Error {
    constructor(path, cause) {
        super(`Could not reach the API (${path}).`);
        this.name = 'ApiNetworkError';
        this.cause = cause;
    }
}
const FALLBACK_MESSAGES = {
    400: 'The request was rejected.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have access to this.',
    404: 'Not found.',
    409: 'That conflicts with something that already exists.',
};
function fallbackCode(status) {
    switch (status) {
        case 400:
            return 'bad_request';
        case 401:
            return 'unauthorized';
        case 403:
            return 'forbidden';
        case 404:
            return 'not_found';
        case 409:
            return 'conflict';
        default:
            return 'internal';
    }
}
/**
 * Builds an `ApiRequestError` from a failed response.
 *
 * Non-envelope bodies (a proxy's HTML 502, an empty 401) still have to produce a usable
 * error, so the status is mapped to the nearest code rather than surfacing a parse failure
 * for what is already a failure.
 */
export async function errorFromResponse(response) {
    let body;
    try {
        body = await response.json();
    }
    catch {
        body = undefined;
    }
    const parsed = apiErrorSchema.safeParse(body);
    if (parsed.success) {
        return new ApiRequestError(parsed.data.error.code, parsed.data.error.message, response.status);
    }
    return new ApiRequestError(fallbackCode(response.status), FALLBACK_MESSAGES[response.status] ?? `Request failed (${response.status}).`, response.status);
}
/** A user-facing message for any throw from the client, without leaking stack details. */
export function messageForError(error) {
    if (error instanceof ApiRequestError)
        return error.message;
    if (error instanceof ApiNetworkError)
        return error.message;
    if (error instanceof ApiParseError)
        return 'The server sent an unexpected response.';
    if (error instanceof Error && error.message)
        return error.message;
    return 'Something went wrong.';
}
//# sourceMappingURL=errors.js.map